import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { BULK_DELETE_LIMIT } from '~/common/constants/max_lengths';
import { analyzerBulkDelete } from '~/components/AnalyzerResultsPage/AnalyzerResultsPage.data';
import DeleteDocumentWithReasonModal from '~/components/Modals/DeleteDocumentWithReasonModal/DeleteDocumentWithReasonModal';
import EcModal from '~/components/Shared/EcModal/EcModal';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { withCurrentUser } from '~/hocs/withCurrentUser';
import { useIntl } from 'react-intl';
import * as selectors from '~/redux/selectors';
import search from '~/redux/slices/search';
import { MODAL_DELETE } from '~/types/modal.types';
import { ERROR, SUCCESS } from '~/types/toast.types';

const DeleteSearchResults = ({
  onActionComplete,
  resultsCache = {},
  currentUser,
}) => {
  const intl = useIntl();
  const modal = useSelector(selectors.selectSearchResultModal);
  const dispatch = useDispatch();
  const hideModal = () => dispatch(search.actions.setModal());
  const pilotSearchQuery = useSelector(selectors.selectPilotSearchQuery);
  const { modalType, context } = modal || {};
  const documentDeleteReasonRequired =
    !!currentUser?.client_config?.document_delete_reason_required;

  if (modalType !== MODAL_DELETE) return null;

  const { isAllRowsSelected, selectedRowIds, totalCount } = context;

  const count = isAllRowsSelected ? totalCount : selectedRowIds.length;

  const renderBulkDeleteModalText = () => {
    let target;
    if (count === 1) {
      const doc = resultsCache[selectedRowIds[0]];
      target = `"${doc.document_handlers[0].document_name}"`;
    } else {
      target = intl.formatMessage(
        {
          id: 'SelectedDocumentsCount',
          defaultMessage:
            '{count} {count, plural, one {document} other {documents}} you have selected',
        },
        { count },
      );
    }
    return intl.formatMessage(
      {
        id: 'DeleteSearchResults.confirmDeleteMessage',
        defaultMessage:
          "Are you sure you want to delete {target}? You won't be able to undo this action.",
        description:
          'Confirmation message for deleting documents. Example: target = "selected documents"',
      },
      { target },
    );
  };

  const onBulkDelete = (reason, extraDetails) => {
    const selectedItems = selectedRowIds.map((id) => resultsCache[id]);

    const handlersIds = isAllRowsSelected
      ? []
      : selectedItems.flatMap((document) =>
          document.document_handlers.map((handler) => handler.id),
        );

    analyzerBulkDelete(
      handlersIds,
      pilotSearchQuery,
      isAllRowsSelected,
      reason,
      extraDetails,
    )
      .then((res) => showToast(SUCCESS, res.detail))
      .catch((err) =>
        showToast(
          ERROR,
          err?.response?.data?.detail ||
            intl.formatMessage({
              id: 'DeleteSearchResults.errorDeleting',
              defaultMessage: 'An error occurred while deleting the documents.',
              description:
                'Error message when document deletion fails. Example: count = "5", count, plural, one {document = "5", documents = "example value"',
            }),
        ),
      )
      .finally(onActionComplete);
  };

  if (documentDeleteReasonRequired) {
    return (
      <DeleteDocumentWithReasonModal
        type="document"
        isVisible={modalType === MODAL_DELETE}
        count={count}
        hideModal={hideModal}
        onConfirmDelete={onBulkDelete}
      />
    );
  }
  return (
    <EcModal
      modalType={modalType}
      width="560px"
      title={intl.formatMessage(
        {
          id: 'DeleteSearchResults.modalTitle',
          defaultMessage:
            'Delete {count, plural, one {Document} other {Documents}}?',
          description:
            'Modal title for deleting documents. Example: count = "3"',
        },
        { count },
      )}
      text={renderBulkDeleteModalText()}
      confirmButtonText={intl.formatMessage({
        id: 'DeleteSearchResults.deleteButton',
        defaultMessage: 'Delete',
        description: 'Delete button text',
      })}
      deleteItem={onBulkDelete}
      hideModal={hideModal}
      overLimit={count > BULK_DELETE_LIMIT}
    />
  );
};

export default withCurrentUser(DeleteSearchResults);
